import { useState } from 'react';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { IoClose as Close } from 'react-icons/io5';
import { BsStopCircle as StopIcon } from 'react-icons/bs';
import { FaRegPlayCircle as StartIcon } from 'react-icons/fa';
import { BsTrash3 as TrashIcon } from 'react-icons/bs';
import { FiAlertTriangle, FiPauseCircle, FiCheckCircle } from 'react-icons/fi';
import CampaignRunService from '@Helpers/URLHelpers/WeboutService/CampaignRunService';
import ErrorHandler from '@Root/ErrorHandler';
import './DeleteCampaignForm.scss';

export default function DeleteCampaignForm(props) {
	const [stopping, setStopping] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const campaignRunService = new CampaignRunService();
	const errorHandler = new ErrorHandler();
	const className = 'deleteCampaignForm';

	const campaign = props?.campaign;
	const status = campaign?.status;
	const isPaused = status === 'paused';
	const isCompleted = status === 'completed';
	const isBusy = stopping || deleting;

	const timeout = (delay) => {
		return new Promise((resolve) => setTimeout(resolve, delay));
	};

	const handleStop = async (event) => {
		event?.preventDefault();
		setStopping(true);
		try {
			const body = {
				id: campaign.id,
				status: 'paused',
			};
			const response = await campaignRunService.updateCampaignRunStatus(body);
			if (response.status < 400) {
				await timeout(500);
				props.reloadData();
				props.closePopup();
			}
		} catch (err) {
			errorHandler.handleError(err);
		}
		setStopping(false);
	};

	const handleStart = async (event) => {
		event?.preventDefault();
		setStopping(true);
		try {
			const body = {
				id: campaign.id,
				status: 'start',
			};
			const response = await campaignRunService.updateCampaignRunStatus(body);
			if (response.status < 400) {
				await timeout(500);
				props.reloadData();
				props.closePopup();
			}
		} catch (err) {
			errorHandler.handleError(err);
		}
		setStopping(false);
	};

	const handleDelete = async (event) => {
		event?.preventDefault();
		setDeleting(true);
		try {
			const body = {
				id: campaign.id,
				status: 'deleted',
			};
			const response = await campaignRunService.deleteCampaignRunStatus(body);
			if (response.status < 400) {
				await timeout(500);
				props.reloadData();
				props.closePopup();
			}
		} catch (err) {
			errorHandler.handleError(err);
		}
		setDeleting(false);
	};

	const getStatusLabel = () => {
		switch (status) {
			case 'paused':
				return 'Paused';
			case 'completed':
				return 'Completed';
			case 'start':
			case 'running':
				return 'Running';
			default:
				return status || 'Unknown';
		}
	};

	return (
		<Dialog
			open={props.open}
			onClose={isBusy ? undefined : props.closePopup}
			maxWidth='md'
			fullWidth
			className={className}
			PaperProps={{
				className: `${className}__paper`,
			}}
		>
			{/* Header */}
			<DialogTitle className={`${className}__header`}>
				<Box className={`${className}__header-content`}>
					<Box className={`${className}__campaign-icon`}>
						<FiPauseCircle />
					</Box>

					<Box className={`${className}__heading`}>
						<Typography className={`${className}__eyebrow`}>
							Campaign settings
						</Typography>

						<Box className={`${className}__title-row`}>
							<Typography className={`${className}__title`}>
								{campaign?.name || 'Unnamed campaign'}
							</Typography>

							<Chip
								label={getStatusLabel()}
								className={`${className}__status ${
									isPaused ? 'paused' : isCompleted ? 'completed' : 'running'
								}`}
								icon={isCompleted ? <FiCheckCircle /> : undefined}
							/>
						</Box>
					</Box>
				</Box>

				<IconButton
					onClick={props.closePopup}
					disabled={isBusy}
					className={`${className}__close`}
					size='small'
				>
					<Close />
				</IconButton>
			</DialogTitle>

			{/* Content */}
			<DialogContent className={`${className}__content`}>
				<Box className={`${className}__intro`}>
					<Typography className={`${className}__intro-title`}>
						Manage campaign
					</Typography>

					<Typography className={`${className}__intro-text`}>
						Choose an action for this campaign.
					</Typography>
				</Box>

                <Box className={`${className}__actions-grid`}>
                    {!isCompleted && (
                        <Box
                            className={`${className}__action-card ${
                                isPaused ? 'start' : 'stop'
                            }`}
                        >
                            <Box className={`${className}__action-icon`}>
                                {isPaused ? <StartIcon /> : <StopIcon />}
                            </Box>

                            <Typography className={`${className}__action-title`}>
                                {isPaused ? 'Start Campaign' : 'Stop Campaign'}
                            </Typography>

                            <Typography className={`${className}__action-description`}>
                                {isPaused
                                    ? 'Resume sending messages to your contacts.'
                                    : 'Stop sending new messages while keeping existing campaign data.'}
                            </Typography>

                            <Button
                                fullWidth
                                variant='outlined'
                                onClick={isPaused ? handleStart : handleStop}
                                disabled={isBusy}
                                className={`${className}__action-button`}
                            >
                                {stopping ? (
                                    <div className='flex-row-center-0p5vw-gap'>
                                        <CircularProgress size={15} color='inherit' />

                                        {isPaused ? 'Starting...' : 'Stopping...'}
                                    </div>
                                ) : (
                                    <div className='flex-row-center-0p5vw-gap'>
                                        {isPaused ? <StartIcon /> : <StopIcon />}
                                        {isPaused ? 'Start Campaign' : 'Stop Campaign'}
                                    </div>
                                )}
                            </Button>
                        </Box>
				    )}
                    <Box className={`${className}__action-card delete`}>
                        <Box className={`${className}__action-icon`}>
                            <TrashIcon />
                        </Box>
                        <Typography className={`${className}__action-title`}>
                            Delete Campaign
                        </Typography>

                        <Typography className={`${className}__action-description`}>
                            Permanently remove the campaign, its history, and associated
                            data.
                        </Typography>

                        <Box className={`${className}__delete-warning`}>
                            <FiAlertTriangle />

                            <Typography>This action cannot be undone.</Typography>
                        </Box>

                        <Button
                            fullWidth
                            variant='contained'
                            color='error'
                            startIcon={
                                deleting ? (
                                    <CircularProgress size={15} color='inherit' />
                                ) : (
                                    <TrashIcon />
                                )
                            }
                            onClick={handleDelete}
                            disabled={isBusy}
                            className={`${className}__action-button delete`}
                        >
                            {deleting ? 'Deleting...' : 'Delete Permanently'}
                        </Button>
                    </Box>
                </Box>
			</DialogContent>

			<DialogActions className={`${className}__footer`}>
				<Button
					onClick={props.closePopup}
					disabled={isBusy}
					variant='text'
					className={`${className}__cancel`}
				>
					Cancel
				</Button>
			</DialogActions>
		</Dialog>
	);
}
